import React, { Component } from "react";
import { Helmet } from 'react-helmet-async';
import "w3-css/3/w3.css";
import "./Temperature.css";
import {
  FromCelsiusButton,
  FromFahrenheitButton,
  FromKelvinButton,
  FromRankineButton,
  FromDelisleButton,
  FromNewtonButton,
  FromReaumurButton,
  FromRomerButton
} from "./Button";
import { getConversion } from "./temp/ConversionTracker";
import { trackConversion, trackButtonClick } from "../../utils/analytics";
export default class Temperatures extends Component {
  render() {
    return (
      <div>
        <Helmet>
          <title>Free Temperature Converter - Convert Celsius, Fahrenheit, Kelvin & More | Kayannou</title>
          <meta name="description"
            content="Free online temperature converter. Convert between Celsius, Fahrenheit, Kelvin, Rankine, Delisle, Newton, Réaumur, and Rømer. Fast, accurate, and easy-to-use temperature conversion tool." />
          <meta name="keywords"
            content="temperature converter, temperature conversion, celsius to fahrenheit, fahrenheit to celsius, kelvin converter, temperature calculator, unit converter, convert temperature, free temperature converter, online temperature tool, celsius, fahrenheit, kelvin, rankine, delisle, newton, réaumur, rømer" />
          <meta name="author" content="Kayannou" />
          <meta property="og:title" content="Free Temperature Converter - Convert Celsius, Fahrenheit, Kelvin & More" />
          <meta property="og:description" content="Free online temperature converter. Convert between Celsius, Fahrenheit, Kelvin, and more. Fast and accurate." />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Free Temperature Converter - Convert Celsius, Fahrenheit, Kelvin & More" />
          <meta name="twitter:description" content="Free online temperature converter. Convert between Celsius, Fahrenheit, Kelvin, and more." />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Temperature Converter",
              "description": "Free online temperature converter for Celsius, Fahrenheit, Kelvin, and more",
              "url": "https://kayannou.com/conversion/temperature",
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </Helmet>
        <Container />
      </div>
    );
  }
}
class Container extends Component {
  state = {
    value: "",
    operation: ""
  };
  handleTempConversionButton = buttonId => {
    this.setState({ operation: buttonId });
    // Track button click
    trackButtonClick(`Temperature: ${buttonId}`, 'Temperature Conversion');
  };

  handleTempConversionValue = inputValue => {
    this.setState({ value: inputValue });
    // Track conversion when both value and operation are present
    if (inputValue && this.state.operation) {
      trackConversion('Temperature', this.state.operation, 'multiple_units', inputValue);
    }
  };

  render() {
    return (
      <div className="temperature-conversion-container">
        <div className="conversion-content">
          <ContainerHeader
            temConversionValue={this.handleTempConversionValue}
            temConversionresult={getConversion(
              this.state.value,
              this.state.operation
            )}
          />
          <MiddleColumn
            id=""
            temConversionButton={this.handleTempConversionButton}
          />
        </div>
      </div>
    );
  }
}
class ContainerHeader extends Component {
  state = {
    inputValue: "",
    isFocused: false
  };

  handleChange = (e) => {
    const value = e.target.value;
    this.setState({ inputValue: value });
    this.props.temConversionValue(value);
  };

  handleFocus = () => {
    this.setState({ isFocused: true });
  };

  handleBlur = () => {
    this.setState({ isFocused: false });
  };

  clearInput = () => {
    this.setState({ inputValue: "" });
    this.props.temConversionValue("");
    if (this.input) {
      this.input.focus();
    }
  };

  render() {
    let result = this.props.temConversionresult;
    const { inputValue, isFocused } = this.state;

    return (
      <div className="conversion-header">
        <div className="conversion-container">
          {/* Input Section */}
          <div className="input-section">
            <div className="input-group">
              <label className="input-label">
                <i className="fa fa-thermometer-half"></i>
                Temperature Input
              </label>
              <div className={`input-wrapper ${isFocused ? 'focused' : ''} ${inputValue ? 'has-value' : ''}`}>
                <input
                  ref={input => (this.input = input)}
                  className="conversion-input"
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  value={inputValue}
                  name="temperature"
                  type="number"
                  placeholder="Enter temperature conversion..."
                  step="any"
                />
                {inputValue && (
                  <button
                    className="clear-button"
                    onClick={this.clearInput}
                    type="button"
                    aria-label="Clear input"
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
                <div className="input-border"></div>
              </div>
            </div>
          </div>

          {/* Result Section */}
          <div className="result-section">
            <div className="result-group">
              <label className="result-label">
                <i className="fa fa-calculator"></i>
                Conversion Result
              </label>
              <div className="result-display">
                {result ? (
                  <div className="result-content">
                    <span className="result-value">{result}</span>
                    <div className="result-indicator">
                      <i className="fa fa-check-circle"></i>
                    </div>
                  </div>
                ) : (
                  <div className="result-placeholder">
                    <i className="fa fa-arrow-left"></i>
                    <span>Enter value and select type</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Visual Enhancement */}
        <div className="header-decoration">
          <div className="decoration-line"></div>
          <div className="decoration-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="decoration-line"></div>
        </div>
      </div>
    );
  }
}
class MiddleColumn extends Component {
  handleClickTemp = buttonId => {
    this.props.temConversionButton(buttonId);
  };

  render() {
    return (
      <div className="conversion-buttons-section">
        <div className="buttons-header">
          <h3 className="section-title">
            <i className="fa fa-thermometer-half"></i>
            Select Temperature Scale
          </h3>
          <p className="section-description">
            Choose the temperature scale you want to convert from
          </p>
        </div>

        <div className="conversion-buttons-grid">
          <FromCelsiusButton id="celto" celTemp={this.handleClickTemp} />
          <FromDelisleButton id="deto" delTemp={this.handleClickTemp} />
          <FromFahrenheitButton id="fato" fahTemp={this.handleClickTemp} />
          <FromKelvinButton id="keto" kelTemp={this.handleClickTemp} />
          <FromNewtonButton id="neto" newTemp={this.handleClickTemp} />
          <FromRankineButton id="rato" ranTemp={this.handleClickTemp} />
          <FromReaumurButton id="reto" reaTemp={this.handleClickTemp} />
          <FromRomerButton id="roto" romTemp={this.handleClickTemp} />
        </div>
      </div>
    );
  }
}
